import { useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  Check,
  ChevronDown,
  ChevronUp,
  Pencil,
  Plus,
  Trash2,
  Upload,
  UserCheck,
  X,
} from 'lucide-react';
import useIsOrgAdmin from 'hooks/useIsOrgAdmin';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { SearchableSelect } from 'components/IncidentConfig/roster/SearchableSelect';
import {
  HWCG_CONTACT_LIST_IDS,
  HWCG_GROUP_LABELS,
  type HwcgContactListId,
} from 'components/IncidentConfig/CreateIncident/hwcgGroupActions';
import { HWCG_COMPANY_OPTIONS } from 'components/IncidentConfig/CreateIncident/hwcgIncidentSpecifics';
import { hwcgRosterPositionNames } from 'components/IncidentConfig/CreateIncident/hwcgRosterTemplates';
import {
  useGetRostersForGroup,
  useRosterTemplatesCatalog,
} from 'hooks/incident/useRoster';
import { HwcgContactImportDialog } from './HwcgContactImportDialog';
import { HwcgMemberCompaniesTable } from './HwcgMemberCompaniesTable';
import {
  useAddHwcgContact,
  useHwcgContactLists,
  useRemoveHwcgContact,
  useSyncHwcgContactLogins,
  useUpdateHwcgContact,
  type HwcgContactListMember,
  type HwcgContactWrite,
  type HwcgPocContact,
} from 'hooks/hwcgContactLists/hwcgContactListHooks';
import { selectGroupGuid, useAppSelector } from 'slices/commonSelectors';
import { useHwcgFlag } from 'utils/featureFlags';

type SortKey =
  | 'list'
  | 'memberCompany'
  | 'serviceProvider'
  | 'firstName'
  | 'lastName'
  | 'email'
  | 'phone'
  | 'primaryCategory'
  | 'secondaryCategory'
  | 'pocContact'
  | 'authorizedIndividual';

type ContactRow = HwcgContactListMember & {
  constituency: HwcgContactListId;
  listLabel: string;
};

function shownList(constituency: string): HwcgContactListId | null {
  if (constituency === 'responsible_party' || constituency === 'mutual_aid') {
    return 'mutual_aid';
  }
  if (constituency === 'eoc' || constituency === 'service_provider') {
    return constituency;
  }
  return null;
}

const COLUMNS: { key: SortKey; label: string }[] = [
  { key: 'memberCompany', label: 'Member company' },
  { key: 'serviceProvider', label: 'Service provider' },
  { key: 'firstName', label: 'First name' },
  { key: 'lastName', label: 'Last name' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'primaryCategory', label: 'Primary category' },
  { key: 'secondaryCategory', label: 'Secondary category' },
  { key: 'pocContact', label: 'POC contact' },
  { key: 'authorizedIndividual', label: 'Authorized individual' },
  { key: 'list', label: 'List' },
];

const EMPTY_FORM: HwcgContactWrite = {
  constituency: 'eoc',
  userGuid: null,
  memberCompany: '',
  serviceProvider: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  primaryCategory: '',
  secondaryCategory: '',
  pocContact: null,
  authorizedIndividual: false,
  memberCompanyExclusive: false,
};

function cell(value: string | null | undefined): string {
  const trimmed = value?.trim() ?? '';
  return trimmed || '—';
}

function pocLabel(value: HwcgPocContact | null): string {
  if (value === 'primary') return 'Primary';
  if (value === 'secondary') return 'Secondary';
  return '—';
}

function sortValue(row: ContactRow, key: SortKey): string {
  if (key === 'list') return row.listLabel;
  if (key === 'pocContact') return pocLabel(row.pocContact);
  if (key === 'authorizedIndividual')
    return row.authorizedIndividual ? 'Yes' : '';
  return row[key] ?? '';
}

function reachableContact(form: HwcgContactWrite): boolean {
  return Boolean(
    form.userGuid ||
      form.email?.trim() ||
      form.phone?.trim() ||
      form.firstName?.trim() ||
      form.lastName?.trim() ||
      form.serviceProvider?.trim()
  );
}

function contactWrite(row: ContactRow): HwcgContactWrite {
  return {
    constituency: row.constituency,
    userGuid: row.userGuid,
    memberCompany: row.memberCompany ?? '',
    serviceProvider: row.serviceProvider ?? '',
    firstName: row.firstName ?? '',
    lastName: row.lastName ?? '',
    email: row.email ?? '',
    phone: row.phone ?? '',
    primaryCategory: row.primaryCategory ?? '',
    secondaryCategory: row.secondaryCategory ?? '',
    pocContact: row.pocContact,
    authorizedIndividual: row.authorizedIndividual === true,
    memberCompanyExclusive: row.memberCompanyExclusive === true,
  };
}

function filterValue(row: ContactRow, key: SortKey): string {
  if (key === 'list') return row.listLabel || '—';
  if (key === 'pocContact') return pocLabel(row.pocContact);
  if (key === 'authorizedIndividual')
    return row.authorizedIndividual ? 'Yes' : '—';
  return cell(row[key]);
}

function columnChoices(rows: ContactRow[], key: SortKey): string[] {
  return [...new Set(rows.map((row) => filterValue(row, key)))].sort(
    (left, right) =>
      left.localeCompare(right, undefined, { sensitivity: 'base' })
  );
}

function matchesColumnFilters(
  row: ContactRow,
  filters: Partial<Record<SortKey, string[]>>
): boolean {
  return COLUMNS.every((column) => {
    const selected = filters[column.key] ?? [];
    if (selected.length === 0) return true;
    return selected.includes(filterValue(row, column.key));
  });
}

function matchesSearch(row: ContactRow, needle: string): boolean {
  if (!needle) return true;
  return [row.name, ...COLUMNS.map((column) => filterValue(row, column.key))]
    .join(' ')
    .toLowerCase()
    .includes(needle);
}

function categoryOptions(positions: string[], current: string) {
  const trimmed = current.trim();
  const names =
    trimmed && !positions.includes(trimmed)
      ? [trimmed, ...positions]
      : positions;
  return names.map((name) => ({ value: name, label: name }));
}

function CategorySelect({
  label,
  value,
  positions,
  onChange,
}: {
  label: string;
  value: string;
  positions: string[];
  onChange: (next: string) => void;
}) {
  const options = categoryOptions(positions, value);
  return (
    <SearchableSelect
      aria-label={label}
      options={options}
      value={options.find((option) => option.value === value.trim()) ?? null}
      clearable
      placeholder="Select position"
      contentClassName="tw:min-w-72"
      onChange={(next) => onChange(next?.value ?? '')}
    />
  );
}

function ContactEditorRow({
  initial,
  roster,
  positions,
  pending,
  cancelLabel,
  onCancel,
  onSave,
}: {
  initial: HwcgContactWrite;
  roster: { user_guid: string; user_name: string; email_address: string }[];
  positions: string[];
  pending: boolean;
  cancelLabel: string;
  onCancel: () => void;
  onSave: (values: HwcgContactWrite) => void;
}) {
  const rowRef = useRef<HTMLTableRowElement>(null);
  const companyListId = useId();
  const [form, setForm] = useState(initial);
  const [userQuery, setUserQuery] = useState('');
  const patch = (next: Partial<HwcgContactWrite>) =>
    setForm((current) => ({ ...current, ...next }));
  const matches = useMemo(() => {
    const needle = userQuery.trim().toLowerCase();
    if (!needle || form.userGuid) return [];
    return roster
      .filter((person) =>
        `${person.user_name} ${person.email_address}`
          .toLowerCase()
          .includes(needle)
      )
      .slice(0, 6);
  }, [form.userGuid, roster, userQuery]);
  const selected = roster.find((person) => person.user_guid === form.userGuid);

  useEffect(() => {
    rowRef.current?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    const field = rowRef.current?.querySelector('input');
    if (field instanceof HTMLInputElement) field.focus();
  }, []);

  return (
    <>
      <TableRow ref={rowRef} className="tw:bg-muted/40">
        <TableCell>
          <Input
            aria-label="Member company"
            list={companyListId}
            value={form.memberCompany ?? ''}
            onChange={(event) => patch({ memberCompany: event.target.value })}
          />
          <datalist id={companyListId}>
            {HWCG_COMPANY_OPTIONS.map((company) => (
              <option key={company} value={company} />
            ))}
          </datalist>
          <label className="tw:mt-1 tw:flex tw:items-center tw:gap-1.5 tw:text-xs tw:text-muted-foreground">
            <Checkbox
              aria-label="Member Company Exclusive"
              checked={form.memberCompanyExclusive === true}
              onCheckedChange={(value) =>
                patch({ memberCompanyExclusive: value === true })
              }
            />
            Exclusive
          </label>
        </TableCell>
        <TableCell>
          <Input
            aria-label="Service provider"
            value={form.serviceProvider ?? ''}
            onChange={(event) => patch({ serviceProvider: event.target.value })}
          />
        </TableCell>
        <TableCell>
          <Input
            aria-label="First name"
            value={form.firstName ?? ''}
            onChange={(event) => patch({ firstName: event.target.value })}
          />
        </TableCell>
        <TableCell>
          <Input
            aria-label="Last name"
            value={form.lastName ?? ''}
            onChange={(event) => patch({ lastName: event.target.value })}
          />
        </TableCell>
        <TableCell>
          <Input
            aria-label="Email"
            value={form.email ?? ''}
            onChange={(event) => patch({ email: event.target.value })}
          />
        </TableCell>
        <TableCell>
          <Input
            aria-label="Phone"
            value={form.phone ?? ''}
            onChange={(event) => patch({ phone: event.target.value })}
          />
        </TableCell>
        <TableCell>
          <CategorySelect
            label="Primary category"
            positions={positions}
            value={form.primaryCategory ?? ''}
            onChange={(primaryCategory) => patch({ primaryCategory })}
          />
        </TableCell>
        <TableCell>
          <CategorySelect
            label="Secondary category"
            positions={positions}
            value={form.secondaryCategory ?? ''}
            onChange={(secondaryCategory) => patch({ secondaryCategory })}
          />
        </TableCell>
        <TableCell>
          <Select
            value={form.pocContact ?? 'none'}
            onValueChange={(value) =>
              patch({
                pocContact: value === 'none' ? null : (value as HwcgPocContact),
              })
            }
          >
            <SelectTrigger aria-label="POC contact" className="tw:w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">—</SelectItem>
              <SelectItem value="primary">Primary</SelectItem>
              <SelectItem value="secondary">Secondary</SelectItem>
            </SelectContent>
          </Select>
        </TableCell>
        <TableCell>
          <Checkbox
            aria-label="Authorized individual"
            checked={form.authorizedIndividual === true}
            onCheckedChange={(value) =>
              patch({ authorizedIndividual: value === true })
            }
          />
        </TableCell>
        <TableCell>
          <Select
            value={form.constituency}
            onValueChange={(value) =>
              patch({ constituency: value as HwcgContactListId })
            }
          >
            <SelectTrigger aria-label="List" className="tw:w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {HWCG_CONTACT_LIST_IDS.map((id) => (
                <SelectItem key={id} value={id}>
                  {HWCG_GROUP_LABELS[id]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </TableCell>
        <TableCell>
          <div className="tw:flex tw:justify-end tw:gap-1">
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label="Save contact"
              disabled={!reachableContact(form) || pending}
              onClick={() => onSave(form)}
            >
              <Check className="tw:size-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label={cancelLabel}
              onClick={onCancel}
            >
              <X className="tw:size-4" />
            </Button>
          </div>
        </TableCell>
      </TableRow>
      <TableRow className="tw:bg-muted/40">
        <TableCell colSpan={COLUMNS.length + 1}>
          <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
            <span className="tw:text-xs tw:text-muted-foreground">
              PRATUS user, optional
            </span>
            {selected ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => patch({ userGuid: null })}
              >
                {selected.user_name || selected.email_address}
                <X className="tw:size-3" />
              </Button>
            ) : (
              <Input
                aria-label="Search workspace members"
                className="tw:max-w-xs"
                value={userQuery}
                placeholder="Search to link a workspace member"
                onChange={(event) => setUserQuery(event.target.value)}
              />
            )}
            {matches.map((person) => (
              <Button
                key={person.user_guid}
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  const [firstName, ...rest] = (person.user_name ?? '')
                    .trim()
                    .split(/\s+/);
                  patch({
                    userGuid: person.user_guid,
                    firstName: form.firstName?.trim()
                      ? form.firstName
                      : firstName || '',
                    lastName: form.lastName?.trim()
                      ? form.lastName
                      : rest.join(' '),
                    email: form.email?.trim()
                      ? form.email
                      : (person.email_address ?? ''),
                  });
                  setUserQuery('');
                }}
              >
                {person.user_name || person.email_address}
              </Button>
            ))}
          </div>
        </TableCell>
      </TableRow>
    </>
  );
}

function ColumnMultiFilter({
  label,
  options,
  selected,
  onChange,
}: {
  label: string;
  options: string[];
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const summary =
    selected.length === 0
      ? 'All'
      : selected.length === 1
        ? selected[0]
        : `${selected.length} selected`;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button
            type="button"
            variant="outline"
            size="sm"
            aria-label={`Filter ${label}`}
            className="tw:h-7 tw:w-full tw:min-w-0 tw:justify-between tw:px-2 tw:font-normal"
          />
        }
      >
        <span className="tw:truncate tw:text-xs">{summary}</span>
        <ChevronDown className="tw:size-3 tw:shrink-0 tw:opacity-60" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="dark tw:max-h-64">
        <DropdownMenuCheckboxItem
          checked={selected.length === 0}
          onCheckedChange={() => onChange([])}
        >
          All
        </DropdownMenuCheckboxItem>
        {options.map((option) => (
          <DropdownMenuCheckboxItem
            key={option}
            checked={selected.includes(option)}
            onCheckedChange={(checked) =>
              onChange(
                checked
                  ? [...selected, option]
                  : selected.filter((value) => value !== option)
              )
            }
          >
            {option}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function HwcgContactListsView() {
  const hwcg = useHwcgFlag();
  const groupGuid = useAppSelector(selectGroupGuid) ?? '';
  const lists = useHwcgContactLists({ enabled: hwcg });
  const roster = useGetRostersForGroup(groupGuid);
  const rosterCatalog = useRosterTemplatesCatalog();
  const positions = useMemo(
    () => hwcgRosterPositionNames(rosterCatalog.data?.templates ?? []),
    [rosterCatalog.data?.templates]
  );
  const addContact = useAddHwcgContact();
  const removeContact = useRemoveHwcgContact();
  const [sort, setSort] = useState<{ key: SortKey; direction: 'asc' | 'desc' }>(
    { key: 'memberCompany', direction: 'asc' }
  );
  const [adding, setAdding] = useState(false);
  const [importing, setImporting] = useState(false);
  const isOrgAdmin = useIsOrgAdmin();
  const syncLogins = useSyncHwcgContactLogins();
  const [editing, setEditing] = useState<ContactRow | null>(null);
  const [columnFilters, setColumnFilters] = useState<
    Partial<Record<SortKey, string[]>>
  >({});
  const [query, setQuery] = useState('');
  const updateContact = useUpdateHwcgContact(editing?.id ?? '');

  const allRows = useMemo(() => {
    return (lists.data ?? []).flatMap((list) => {
      const constituency = shownList(list.constituency);
      if (!constituency) return [];
      return list.members.map((member) => ({
        ...member,
        constituency,
        listLabel: HWCG_GROUP_LABELS[constituency],
      }));
    });
  }, [lists.data]);

  const columnOptions = useMemo(() => {
    const options = {} as Record<SortKey, string[]>;
    for (const column of COLUMNS) {
      options[column.key] = columnChoices(allRows, column.key);
    }
    return options;
  }, [allRows]);

  const needle = query.trim().toLowerCase();
  const filtersActive =
    !!needle ||
    COLUMNS.some((column) => (columnFilters[column.key]?.length ?? 0) > 0);

  const rows = useMemo(() => {
    const direction = sort.direction === 'asc' ? 1 : -1;
    return allRows
      .filter(
        (row) =>
          editing?.id === row.id ||
          (matchesColumnFilters(row, columnFilters) &&
            matchesSearch(row, needle))
      )
      .sort((left, right) => {
        const compared = sortValue(left, sort.key).localeCompare(
          sortValue(right, sort.key),
          undefined,
          { sensitivity: 'base' }
        );
        return compared * direction;
      });
  }, [allRows, columnFilters, editing, needle, sort]);

  if (!hwcg) return null;

  const toggleSort = (key: SortKey) => {
    setSort((current) =>
      current.key === key
        ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' }
    );
  };

  return (
    <div className="tw:flex tw:h-full tw:min-h-0 tw:min-w-0 tw:max-w-full tw:flex-col tw:gap-4 tw:overflow-x-hidden tw:overflow-y-auto tw:p-4">
      <HwcgMemberCompaniesTable />

      <div className="tw:flex tw:items-center tw:justify-between tw:gap-3">
        <h1 className="tw:text-lg tw:font-semibold">Contact List</h1>
        <div className="tw:flex tw:items-center tw:gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => setImporting(true)}
          >
            <Upload className="tw:size-4" />
            Import
          </Button>
          {isOrgAdmin ? (
            <Button
              type="button"
              variant="outline"
              disabled={syncLogins.isPending}
              title="Make every contact with an email, and every external member, an internal workspace member with a PRATUS login invite"
              onClick={() => syncLogins.mutate()}
            >
              <UserCheck className="tw:size-4" />
              {syncLogins.isPending ? 'Syncing…' : 'Sync PRATUS logins'}
            </Button>
          ) : null}
          <Button
            type="button"
            disabled={adding}
            onClick={() => {
              setEditing(null);
              setAdding(true);
            }}
          >
            <Plus className="tw:size-4" />
            Add contact
          </Button>
        </div>
      </div>
      <HwcgContactImportDialog open={importing} onOpenChange={setImporting} />

      <Input
        type="search"
        aria-label="Search contacts"
        placeholder="Search contacts"
        className="tw:max-w-xs"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
      />

      <div className="tw:min-w-0 tw:max-w-full tw:shrink-0 tw:overflow-hidden tw:rounded-lg tw:border-2 tw:border-solid tw:border-foreground/70 tw:bg-card tw:[&_[data-slot=table-container]]:pb-3">
        {lists.isLoading ? (
          <p className="tw:p-4 tw:text-sm tw:text-muted-foreground">
            Loading contacts…
          </p>
        ) : null}
        {lists.isError ? (
          <p className="tw:p-4 tw:text-sm tw:text-destructive">
            Contact list could not be loaded.
          </p>
        ) : null}
        {!lists.isLoading && !lists.isError ? (
          <Table>
            <TableHeader>
              <TableRow>
                {COLUMNS.map((column) => (
                  <TableHead
                    key={column.key}
                    className="tw:h-auto tw:min-w-36 tw:align-top tw:py-2"
                  >
                    <div className="tw:flex tw:flex-col tw:gap-1">
                      <button
                        type="button"
                        className="tw:inline-flex tw:items-center tw:gap-1 tw:uppercase"
                        onClick={() => toggleSort(column.key)}
                      >
                        {column.label}
                        {sort.key === column.key ? (
                          sort.direction === 'asc' ? (
                            <ChevronUp className="tw:size-3" />
                          ) : (
                            <ChevronDown className="tw:size-3" />
                          )
                        ) : (
                          <ChevronUp className="tw:size-3 tw:opacity-30" />
                        )}
                      </button>
                      <ColumnMultiFilter
                        label={column.label}
                        options={columnOptions[column.key]}
                        selected={columnFilters[column.key] ?? []}
                        onChange={(next) =>
                          setColumnFilters((current) => ({
                            ...current,
                            [column.key]: next,
                          }))
                        }
                      />
                    </div>
                  </TableHead>
                ))}
                <TableHead className="tw:w-20">
                  <span className="tw:sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {adding ? (
                <ContactEditorRow
                  initial={EMPTY_FORM}
                  roster={roster.data ?? []}
                  positions={positions}
                  pending={addContact.isPending}
                  cancelLabel="Cancel new contact"
                  onCancel={() => setAdding(false)}
                  onSave={(values) =>
                    addContact.mutate(values, {
                      onSuccess: () => setAdding(false),
                    })
                  }
                />
              ) : null}
              {rows.length === 0 && !adding ? (
                <TableRow>
                  <TableCell
                    colSpan={COLUMNS.length + 1}
                    className="tw:text-muted-foreground"
                  >
                    {filtersActive ? 'No contacts match.' : 'No contacts yet.'}
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) =>
                  editing?.id === row.id ? (
                    <ContactEditorRow
                      key={row.id}
                      initial={contactWrite(row)}
                      roster={roster.data ?? []}
                      positions={positions}
                      pending={updateContact.isPending}
                      cancelLabel={`Cancel edit ${row.name}`}
                      onCancel={() => setEditing(null)}
                      onSave={(values) =>
                        updateContact.mutate(values, {
                          onSuccess: () => setEditing(null),
                        })
                      }
                    />
                  ) : (
                    <TableRow
                      key={row.id}
                      className="tw:cursor-pointer"
                      onClick={() => {
                        setAdding(false);
                        setEditing(row);
                      }}
                    >
                      <TableCell>
                        <span className="tw:flex tw:items-center tw:gap-1.5">
                          {cell(row.memberCompany)}
                          {row.memberCompanyExclusive ? (
                            <Badge
                              variant="outline"
                              title="Member Company Exclusive: only activated when this company is the Responsible Party"
                            >
                              Exclusive
                            </Badge>
                          ) : null}
                        </span>
                      </TableCell>
                      <TableCell>{cell(row.serviceProvider)}</TableCell>
                      <TableCell>{cell(row.firstName)}</TableCell>
                      <TableCell>{cell(row.lastName)}</TableCell>
                      <TableCell>{cell(row.email)}</TableCell>
                      <TableCell>{cell(row.phone)}</TableCell>
                      <TableCell>{cell(row.primaryCategory)}</TableCell>
                      <TableCell>{cell(row.secondaryCategory)}</TableCell>
                      <TableCell>{pocLabel(row.pocContact)}</TableCell>
                      <TableCell>
                        {row.authorizedIndividual ? 'Yes' : '—'}
                      </TableCell>
                      <TableCell>{row.listLabel}</TableCell>
                      <TableCell>
                        <div className="tw:flex tw:justify-end tw:gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon-sm"
                            aria-label={`Edit ${row.name}`}
                            onClick={() => {
                              setAdding(false);
                              setEditing(row);
                            }}
                          >
                            <Pencil className="tw:size-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon-sm"
                            aria-label={`Remove ${row.name}`}
                            disabled={removeContact.isPending}
                            onClick={(event) => {
                              event.stopPropagation();
                              removeContact.mutate(row.id);
                            }}
                          >
                            <Trash2 className="tw:size-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  )
                )
              )}
            </TableBody>
          </Table>
        ) : null}
      </div>
    </div>
  );
}
